import $ from 'jquery';
import { __ } from '@wordpress/i18n';
import 'owl.carousel';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import '../sass/style.scss';

const initFaqAccordion = () => {
    const faqBlocks = document.querySelectorAll('.ls-faq');

    faqBlocks.forEach( ( block ) => {
        const items = block.querySelectorAll('.ls-faq__item');

        items.forEach( ( item, index ) => {
            const trigger = item.querySelector('.ls-faq__trigger');
            const panel = item.querySelector('.ls-faq__panel');

            if ( ! trigger || ! panel ) {
                return;
            }

            panel.setAttribute('hidden', '');
            trigger.setAttribute('aria-expanded', 'false');

            trigger.addEventListener('click', () => {
                const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

                if ( ! block.hasAttribute('data-allow-multiple') ) {
                    items.forEach( ( sibling ) => {
                        if ( sibling === item ) {
                            return;
                        }

                        const siblingTrigger = sibling.querySelector('.ls-faq__trigger');
                        const siblingPanel = sibling.querySelector('.ls-faq__panel');

                        if ( siblingTrigger && siblingPanel ) {
                            siblingTrigger.setAttribute('aria-expanded', 'false');
                            sibling.classList.remove('is-open');
                            siblingPanel.classList.remove('is-open');
                            siblingPanel.setAttribute('hidden', '');
                        }
                    } );
                }

                if ( isExpanded ) {
                    trigger.setAttribute('aria-expanded', 'false');
                    item.classList.remove('is-open');
                    panel.classList.remove('is-open');
                    panel.setAttribute('hidden', '');
                } else {
                    trigger.setAttribute('aria-expanded', 'true');
                    item.classList.add('is-open');
                    panel.classList.add('is-open');
                    panel.removeAttribute('hidden');
                }
            } );

            if ( index === 0 ) {
                trigger.setAttribute('aria-expanded', 'true');
                item.classList.add('is-open');
                panel.classList.add('is-open');
                panel.removeAttribute('hidden');
            }
        } );
    } );
};

const initPrimaryMenu = () => {
    const menus = document.querySelectorAll('.ls-primary-menu');
    const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    menus.forEach( ( menu, index ) => {
        if ( menu.dataset.lsPrimaryInit === 'true' ) {
            return;
        }

        const toggle = menu.querySelector('.ls-primary-menu__toggle');
        const nav = menu.querySelector('.ls-primary-menu__nav');
        const panel = menu.querySelector('.ls-primary-menu__panel');
        const panelClose = menu.querySelector('.ls-primary-menu__panel-close');
        const backdrop = menu.querySelector('.ls-primary-menu__backdrop');
        const ctaContainer = menu.querySelector('.ls-primary-menu__mobile-cta');
        const menuList = menu.querySelector('.ls-primary-menu__list');

        if ( ! toggle || ! nav || ! panel || ! panelClose || ! backdrop || ! menuList ) {
            return;
        }

        menu.dataset.lsPrimaryInit = 'true';

        const navId = nav.id || menu.dataset.menuId || `ls-primary-menu-${ index }`;
        const panelId = panel.id || nav.dataset.panelId || `${ navId }-panel`;

        nav.id = navId;
        panel.id = panelId;
        toggle.setAttribute('aria-controls', panelId);
        toggle.setAttribute('aria-haspopup', 'dialog');
        panelClose.setAttribute('aria-controls', panelId);

        const rawBreakpoint = parseInt( menu.dataset.breakpoint || '1179', 10 );
        const breakpoint = Number.isFinite( rawBreakpoint ) ? rawBreakpoint : 1179;
        const mediaQuery = window.matchMedia( `(max-width: ${ breakpoint }px)` );
        const panelLabel = panel.getAttribute( 'aria-label' ) || 'Menu';

        let isOpen = false;
        let lastFocusedElement = null;

        const getFocusableElements = () => {
            return Array.from( panel.querySelectorAll( focusableSelector ) ).filter( ( element ) => {
                return ! element.hasAttribute( 'disabled' );
            } );
        };

        const closeSubmenu = ( item ) => {
            const trigger = item.querySelector(':scope > a');
            const submenu = item.querySelector(':scope > .sub-menu');

            if ( ! trigger || ! submenu ) {
                return;
            }

            item.classList.remove( 'is-open' );
            trigger.setAttribute( 'aria-expanded', 'false' );
            submenu.setAttribute( 'hidden', '' );
        };

        const closeAllSubmenus = ( exceptItem = null ) => {
            menuList.querySelectorAll( '.menu-item-has-children' ).forEach( ( item ) => {
                if ( exceptItem && item === exceptItem ) {
                    return;
                }

                closeSubmenu( item );
            } );
        };

        const openSubmenu = ( item ) => {
            const trigger = item.querySelector(':scope > a');
            const submenu = item.querySelector(':scope > .sub-menu');

            if ( ! trigger || ! submenu ) {
                return;
            }

            closeAllSubmenus( item );
            item.classList.add( 'is-open' );
            trigger.setAttribute( 'aria-expanded', 'true' );
            submenu.removeAttribute( 'hidden' );
        };

        const setDesktopMenu = () => {
            closeAllSubmenus();
            menuList.querySelectorAll( '.menu-item-has-children > .sub-menu' ).forEach( ( submenu ) => {
                submenu.removeAttribute( 'hidden' );
            } );

            panel.hidden = false;
            backdrop.hidden = true;
            nav.setAttribute( 'aria-hidden', 'false' );
            toggle.setAttribute( 'aria-expanded', 'false' );
            panel.removeAttribute( 'aria-modal' );
            panel.removeAttribute( 'role' );
            menu.classList.remove( 'is-open' );
            document.body.classList.remove( 'ls-mobile-menu-open' );
        };

        const setMobileMenu = () => {
            closeAllSubmenus();
            panel.hidden = true;
            backdrop.hidden = true;
            nav.setAttribute( 'aria-hidden', 'true' );
            toggle.setAttribute( 'aria-expanded', 'false' );
            panel.setAttribute( 'role', 'dialog' );
            panel.setAttribute( 'aria-modal', 'true' );
            panel.setAttribute( 'aria-label', panelLabel );
            menu.classList.remove( 'is-open' );
        };

        const closeMenu = ( restoreFocus = true ) => {
            if ( ! isOpen ) {
                return;
            }

            isOpen = false;
            toggle.setAttribute( 'aria-expanded', 'false' );
            nav.setAttribute( 'aria-hidden', 'true' );
            menu.classList.remove( 'is-open' );
            document.body.classList.remove( 'ls-mobile-menu-open' );

            window.setTimeout( () => {
                if ( ! isOpen && mediaQuery.matches ) {
                    panel.hidden = true;
                    backdrop.hidden = true;
                }
            }, 320 );

            if ( restoreFocus && lastFocusedElement instanceof HTMLElement ) {
                lastFocusedElement.focus();
            }
        };

        const openMenu = () => {
            if ( ! mediaQuery.matches || isOpen ) {
                return;
            }

            isOpen = true;
            lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            panel.hidden = false;
            backdrop.hidden = false;
            toggle.setAttribute( 'aria-expanded', 'true' );
            nav.setAttribute( 'aria-hidden', 'false' );
            menu.classList.add( 'is-open' );
            document.body.classList.add( 'ls-mobile-menu-open' );

            window.requestAnimationFrame( () => {
                panelClose.focus();
            } );
        };

        const ensureMobileCta = () => {
            if ( ! ctaContainer || ctaContainer.dataset.lsPrimaryCtaInit === 'true' ) {
                return;
            }

            ctaContainer.dataset.lsPrimaryCtaInit = 'true';

            const ctaLink = menuList.querySelector( '.tele > a' );
            if ( ! ctaLink ) {
                return;
            }

            ctaContainer.replaceChildren( ctaLink.cloneNode( true ) );
        };

        const initMobileAccordion = () => {
            menuList.querySelectorAll( '.menu-item-has-children' ).forEach( ( item, submenuIndex ) => {
                if ( item.dataset.lsPrimaryAccordionInit === 'true' ) {
                    return;
                }

                const trigger = item.querySelector(':scope > a');
                const submenu = item.querySelector(':scope > .sub-menu');

                if ( ! trigger || ! submenu ) {
                    return;
                }

                item.dataset.lsPrimaryAccordionInit = 'true';

                const submenuId = submenu.id || `${ navId }-submenu-${ submenuIndex }`;
                submenu.id = submenuId;
                trigger.setAttribute( 'aria-controls', submenuId );
                trigger.setAttribute( 'aria-expanded', 'false' );
                submenu.setAttribute( 'hidden', '' );

                trigger.addEventListener( 'click', ( event ) => {
                    if ( ! mediaQuery.matches ) {
                        return;
                    }

                    event.preventDefault();

                    if ( item.classList.contains( 'is-open' ) ) {
                        closeSubmenu( item );
                        return;
                    }

                    openSubmenu( item );
                } );
            } );
        };

        const handleKeyDown = ( event ) => {
            if ( ! isOpen || ! mediaQuery.matches ) {
                return;
            }

            if ( event.key === 'Escape' || event.key === 'Esc' ) {
                event.preventDefault();
                closeMenu( false );
                toggle.focus();
                return;
            }

            if ( event.key !== 'Tab' ) {
                return;
            }

            const focusableElements = getFocusableElements();
            if ( focusableElements.length === 0 ) {
                event.preventDefault();
                return;
            }

            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[ focusableElements.length - 1 ];

            if ( event.shiftKey && document.activeElement === firstFocusable ) {
                event.preventDefault();
                lastFocusable.focus();
            } else if ( ! event.shiftKey && document.activeElement === lastFocusable ) {
                event.preventDefault();
                firstFocusable.focus();
            }
        };

        const syncWithViewport = ( matchesMobile ) => {
            closeMenu( false );

            if ( matchesMobile ) {
                setMobileMenu();
            } else {
                setDesktopMenu();
            }
        };

        ensureMobileCta();
        initMobileAccordion();
        syncWithViewport( mediaQuery.matches );

        document.addEventListener( 'keydown', handleKeyDown );

        panelClose.addEventListener( 'click', () => {
            closeMenu( false );
            toggle.focus();
        } );

        backdrop.addEventListener( 'click', () => {
            closeMenu( false );
            toggle.focus();
        } );

        if ( typeof mediaQuery.addEventListener === 'function' ) {
            mediaQuery.addEventListener( 'change', ( event ) => {
                syncWithViewport( event.matches );
            } );
        } else if ( typeof mediaQuery.addListener === 'function' ) {
            mediaQuery.addListener( ( event ) => {
                syncWithViewport( event.matches );
            } );
        }

        toggle.addEventListener( 'click', () => {
            if ( isOpen ) {
                closeMenu( false );
                return;
            }

            openMenu();
        } );

        panel.addEventListener( 'click', ( event ) => {
            if ( ! mediaQuery.matches ) {
                return;
            }

            const clickedLink = event.target.closest( 'a' );
            if ( ! clickedLink || ! panel.contains( clickedLink ) ) {
                return;
            }

            const parentWithChildren = clickedLink.closest( '.menu-item-has-children' );
            const directParentLink = parentWithChildren?.querySelector(':scope > a');

            if ( directParentLink === clickedLink ) {
                return;
            }

            closeMenu( false );
        } );
    } );
};

const initStickyHeader = () => {
    const header = document.querySelector( 'header.wp-block-template-part' );

    if ( ! header ) {
        return;
    }

    const updateHeaderState = () => {
        header.classList.toggle( 'is-scrolled', window.scrollY >= 1 );
    };

    updateHeaderState();
    window.addEventListener( 'scroll', updateHeaderState, { passive: true } );
};

const initMegaMenu = () => {
    const menus = document.querySelectorAll('.ls-menu-special');

    menus.forEach( ( menu ) => {
        if ( menu.dataset.lsMegaInit === 'true' ) {
            return;
        }

        menu.dataset.lsMegaInit = 'true';

        const closeItem = ( item ) => {
            const trigger = item.querySelector('.ls-menu-special__link');
            const panel = item.querySelector('.ls-menu-special__mega, .ls-menu-special__list-content');

            if ( trigger ) {
                trigger.setAttribute('aria-expanded', 'false');
            }

            if ( panel ) {
                panel.setAttribute('hidden', '');
            }

            item.classList.remove('is-open');
        };

        const items = menu.querySelectorAll('.ls-menu-special__item.is-mega, .ls-menu-special__item.has-list');

        items.forEach( ( item, index ) => {
            const trigger = item.querySelector('.ls-menu-special__link');
            const panel = item.querySelector('.ls-menu-special__mega, .ls-menu-special__list-content');

            if ( ! trigger || ! panel ) {
                return;
            }

            let closeTimeout;

            const panelId = panel.id || `ls-mega-panel-${ index }-${ Math.random().toString(36).slice(2, 8) }`;

            panel.id = panelId;
            panel.setAttribute('hidden', '');
            trigger.setAttribute('aria-controls', panelId);
            trigger.setAttribute('aria-haspopup', 'true');
            trigger.setAttribute('aria-expanded', 'false');

            const cancelClose = () => {
                if ( closeTimeout ) {
                    window.clearTimeout( closeTimeout );
                }
            };

            const open = () => {
                menu.querySelectorAll('.ls-menu-special__item.is-open').forEach( ( openItem ) => {
                    if ( openItem !== item ) {
                        closeItem( openItem );
                    }
                } );

                cancelClose();
                item.classList.add('is-open');
                trigger.setAttribute('aria-expanded', 'true');
                panel.removeAttribute('hidden');
            };

            const close = () => {
                cancelClose();
                closeTimeout = window.setTimeout( () => {
                    closeItem( item );
                }, 220 );
            };

            const maybeClose = ( event ) => {
                const nextTarget = event?.relatedTarget;

                if ( nextTarget instanceof Node && item.contains( nextTarget ) ) {
                    return;
                }

                close();
            };

            item.addEventListener('mouseenter', open );
            item.addEventListener('mouseleave', maybeClose );
            panel.addEventListener('mouseenter', open );
            panel.addEventListener('mouseleave', maybeClose );

            trigger.addEventListener('focus', open );
            trigger.addEventListener('blur', ( event ) => {
                if ( ! item.contains( event.relatedTarget ) ) {
                    close();
                }
            } );

            panel.addEventListener('focusin', open );
            panel.addEventListener('focusout', ( event ) => {
                if ( ! panel.contains( event.relatedTarget ) ) {
                    close();
                }
            } );

            closeItem( item );
        } );
    } );
};

const initSliders = () => {
    const jQueryRef = window?.jQuery || $;

    if ( ! jQueryRef || ! jQueryRef.fn || typeof jQueryRef.fn.owlCarousel !== 'function' ) {
        return;
    }

    const navText = [
        `<span class="ls-slider__nav-icon" aria-hidden="true">‹</span><span class="screen-reader-text">${ __( 'Previous slide', 'wmeri' ) }</span>`,
        `<span class="ls-slider__nav-icon" aria-hidden="true">›</span><span class="screen-reader-text">${ __( 'Next slide', 'wmeri' ) }</span>`,
    ];

    const baseOptions = {
        items: 1,
        loop: true,
        margin: 24,
        nav: true,
        dots: true,
        navText,
        smartSpeed: 450,
    };

    const ensureCarousel = ( element, overrides = {} ) => {
        if ( ! element || element.dataset.lsSliderInit === 'true' ) {
            return;
        }

        const $element = jQueryRef( element );

        if ( ! $element.length ) {
            return;
        }

        const options = { ...baseOptions, ...overrides };

        element.dataset.lsSliderInit = 'true';
        $element.addClass( 'owl-carousel' );
        $element.owlCarousel( options );
    };

    document.querySelectorAll( '.ls-slider__track' ).forEach( ( track ) => {
        ensureCarousel( track );
    } );

    document.querySelectorAll( '.ls-slider-dynamic__track' ).forEach( ( track ) => {
        ensureCarousel( track, {
            autoplay: true,
            autoplayTimeout: 6000,
            autoplayHoverPause: true,
        } );
    } );
};

const initTestimonialsSlider = () => {
    const sliders = document.querySelectorAll( '.ls-testimonials-slider' );

    sliders.forEach( ( slider ) => {
        if ( slider.dataset.lsTestimonialsInit === 'true' ) {
            return;
        }

        const panels = Array.from( slider.querySelectorAll( '.ls-testimonials-slider__slide' ) );
        const previousButton = slider.querySelector( '.ls-testimonials-slider__nav--prev' );
        const nextButton = slider.querySelector( '.ls-testimonials-slider__nav--next' );

        if ( ! panels.length || ! previousButton || ! nextButton ) {
            return;
        }

        slider.dataset.lsTestimonialsInit = 'true';

        const autoplay = slider.dataset.autoplay === 'true';
        const manualScroll = slider.dataset.manualScroll !== 'false';
        const motionQuery = window.matchMedia ? window.matchMedia( '(prefers-reduced-motion: reduce)' ) : null;
        const allowAutoplay = autoplay && ! motionQuery?.matches && panels.length > 1;
        const transitionTimeRaw = parseInt( slider.dataset.transitionTime || '5000', 10 );
        const transitionTime = Number.isFinite( transitionTimeRaw ) ? Math.max( 1500, transitionTimeRaw ) : 5000;
        let activeIndex = Math.max( 0, panels.findIndex( ( panel ) => panel.classList.contains( 'is-active' ) ) );
        let autoplayInterval = null;

        const applyState = ( nextIndex ) => {
            if ( ! panels[nextIndex] ) {
                return;
            }

            activeIndex = nextIndex;

            panels.forEach( ( panel, index ) => {
                const isActive = index === activeIndex;
                panel.classList.toggle( 'is-active', isActive );

                if ( isActive ) {
                    panel.removeAttribute( 'hidden' );
                } else {
                    panel.setAttribute( 'hidden', '' );
                }
            } );
        };

        const next = () => {
            applyState( ( activeIndex + 1 ) % panels.length );
        };

        const previous = () => {
            applyState( ( activeIndex - 1 + panels.length ) % panels.length );
        };

        if ( manualScroll ) {
            previousButton.addEventListener( 'click', previous );
            nextButton.addEventListener( 'click', next );

            slider.addEventListener( 'keydown', ( event ) => {
                if ( event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' ) {
                    return;
                }

                event.preventDefault();

                if ( event.key === 'ArrowLeft' ) {
                    previous();
                } else {
                    next();
                }
            } );
        } else {
            previousButton.setAttribute( 'disabled', 'disabled' );
            nextButton.setAttribute( 'disabled', 'disabled' );
        }

        const startAutoplay = () => {
            if ( ! allowAutoplay || autoplayInterval ) {
                return;
            }

            autoplayInterval = window.setInterval( next, transitionTime );
        };

        const stopAutoplay = () => {
            if ( ! autoplayInterval ) {
                return;
            }

            window.clearInterval( autoplayInterval );
            autoplayInterval = null;
        };

        slider.addEventListener( 'mouseenter', stopAutoplay );
        slider.addEventListener( 'mouseleave', startAutoplay );
        slider.addEventListener( 'focusin', stopAutoplay );
        slider.addEventListener( 'focusout', ( event ) => {
            if ( ! slider.contains( event.relatedTarget ) ) {
                startAutoplay();
            }
        } );

        applyState( activeIndex );
        startAutoplay();
    } );
};

const initPatientReviews = () => {
    const blocks = document.querySelectorAll( '.ls-patient-reviews' );

    blocks.forEach( ( block ) => {
        if ( block.dataset.lsPatientReviewsInit === 'true' ) {
            return;
        }

        const slides = Array.from( block.querySelectorAll( '.ls-patient-reviews__slide' ) );

        if ( ! slides.length ) {
            return;
        }

        block.dataset.lsPatientReviewsInit = 'true';

        const previousButton = block.querySelector( '.ls-patient-reviews__nav--prev' );
        const nextButton = block.querySelector( '.ls-patient-reviews__nav--next' );
        const autoplay = block.dataset.autoplay === 'true';
        const motionQuery = window.matchMedia ? window.matchMedia( '(prefers-reduced-motion: reduce)' ) : null;
        const allowAutoplay = autoplay && ! motionQuery?.matches && slides.length > 1;
        const transitionTimeRaw = parseInt( block.dataset.transitionTime || '6000', 10 );
        const transitionTime = Number.isFinite( transitionTimeRaw ) ? Math.max( 2000, transitionTimeRaw ) : 6000;
        let activeIndex = Math.max( 0, slides.findIndex( ( slide ) => slide.classList.contains( 'is-active' ) ) );
        let autoplayInterval = null;

        const applyState = ( nextIndex ) => {
            if ( ! slides[ nextIndex ] ) {
                return;
            }

            activeIndex = nextIndex;

            slides.forEach( ( slide, index ) => {
                const isActive = index === activeIndex;
                slide.classList.toggle( 'is-active', isActive );

                if ( isActive ) {
                    slide.removeAttribute( 'hidden' );
                } else {
                    slide.setAttribute( 'hidden', '' );
                }
            } );
        };

        const next = () => {
            applyState( ( activeIndex + 1 ) % slides.length );
        };

        const previous = () => {
            applyState( ( activeIndex - 1 + slides.length ) % slides.length );
        };

        previousButton?.addEventListener( 'click', previous );
        nextButton?.addEventListener( 'click', next );

        const startAutoplay = () => {
            if ( ! allowAutoplay || autoplayInterval ) {
                return;
            }

            autoplayInterval = window.setInterval( next, transitionTime );
        };

        const stopAutoplay = () => {
            if ( ! autoplayInterval ) {
                return;
            }

            window.clearInterval( autoplayInterval );
            autoplayInterval = null;
        };

        block.addEventListener( 'mouseenter', stopAutoplay );
        block.addEventListener( 'mouseleave', startAutoplay );
        block.addEventListener( 'focusin', stopAutoplay );
        block.addEventListener( 'focusout', ( event ) => {
            if ( ! block.contains( event.relatedTarget ) ) {
                startAutoplay();
            }
        } );

        applyState( activeIndex );
        startAutoplay();
    } );
};

const initBeforeAfterSliders = () => {
    const clamp = ( value, min, max ) => Math.max( min, Math.min( max, value ) );

    const getClientX = ( event ) => {
        if ( typeof event.clientX === 'number' ) {
            return event.clientX;
        }

        if ( event.touches?.length ) {
            return event.touches[0].clientX;
        }

        if ( event.changedTouches?.length ) {
            return event.changedTouches[0].clientX;
        }

        return null;
    };

    document.querySelectorAll( '.ba-slider' ).forEach( ( slider ) => {
        if ( slider.dataset.baInit === 'true' ) {
            return;
        }

        const handle = slider.querySelector( '.ba-handle' );

        if ( ! slider.querySelector( '.ba-image--after' ) || ! handle ) {
            return;
        }

        slider.dataset.baInit = 'true';

        const startPositionRaw = parseFloat( slider.dataset.startPosition || '50' );
        const startPosition = Number.isFinite( startPositionRaw ) ? clamp( startPositionRaw, 0, 100 ) : 50;

        let isDragging = false;

        const setPosition = ( percent ) => {
            const nextPosition = clamp( percent, 0, 100 );
            const value = `${ nextPosition }%`;

            slider.style.setProperty( '--ba-position', value );
            handle.setAttribute( 'aria-valuenow', String( Math.round( nextPosition ) ) );
        };

        const updatePositionFromEvent = ( event ) => {
            const clientX = getClientX( event );

            if ( clientX === null ) {
                return;
            }

            const rect = slider.getBoundingClientRect();

            if ( ! rect.width ) {
                return;
            }

            const nextPosition = ( ( clientX - rect.left ) / rect.width ) * 100;
            setPosition( nextPosition );
        };

        const onPointerMove = ( event ) => {
            if ( ! isDragging ) {
                return;
            }

            event.preventDefault();
            updatePositionFromEvent( event );
        };

        const stopDragging = () => {
            if ( ! isDragging ) {
                return;
            }

            isDragging = false;
            slider.classList.remove( 'is-dragging' );
        };

        const startDragging = ( event ) => {
            if ( event.type === 'mousedown' && event.button !== 0 ) {
                return;
            }

            if ( event.type === 'touchstart' ) {
                event.preventDefault();
            }

            isDragging = true;
            slider.classList.add( 'is-dragging' );
            updatePositionFromEvent( event );
        };

        slider.addEventListener( 'mousedown', startDragging );
        slider.addEventListener( 'touchstart', startDragging, { passive: false } );
        window.addEventListener( 'mousemove', onPointerMove );
        window.addEventListener( 'touchmove', onPointerMove, { passive: false } );
        window.addEventListener( 'mouseup', stopDragging );
        window.addEventListener( 'touchend', stopDragging );
        window.addEventListener( 'touchcancel', stopDragging );
        slider.addEventListener( 'mouseleave', stopDragging );

        setPosition( startPosition );
    } );
};

const activateTab = ( tab, tabs, panels ) => {
    const controls = tab.getAttribute('aria-controls');
    const panel = controls ? document.getElementById(controls) : null;

    tabs.forEach( ( currentTab ) => {
        currentTab.classList.remove('is-active');
        currentTab.setAttribute('aria-selected', 'false');
        currentTab.setAttribute('tabindex', '-1');
    } );

    panels.forEach( ( currentPanel ) => {
        currentPanel.classList.remove('is-active');
        currentPanel.setAttribute('hidden', '');
    } );

    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');

    if ( panel ) {
        panel.classList.add('is-active');
        panel.removeAttribute('hidden');
    }
};

const handleTabKeyboardNavigation = ( event, tabs ) => {
    const currentIndex = tabs.indexOf(event.currentTarget);

    if ( currentIndex === -1 ) {
        return;
    }

    const focusTab = ( index ) => {
        const tabToFocus = tabs[index];

        if ( tabToFocus ) {
            tabToFocus.focus();
        }
    };

    switch ( event.key ) {
        case 'ArrowRight':
        case 'ArrowDown':
            event.preventDefault();
            focusTab( ( currentIndex + 1 ) % tabs.length );
            break;
        case 'ArrowLeft':
        case 'ArrowUp':
            event.preventDefault();
            focusTab( ( currentIndex - 1 + tabs.length ) % tabs.length );
            break;
        default:
            break;
    }
};

const initTabs = () => {
    const tabContainers = document.querySelectorAll('.ls-tabs');

    tabContainers.forEach( ( container ) => {
        const tabs = Array.from( container.querySelectorAll('.ls-tabs__nav-item') );
        const panels = Array.from( container.querySelectorAll('.ls-tabs__panel') );

        if ( ! tabs.length || ! panels.length ) {
            return;
        }

        tabs.forEach( ( tab ) => {
            tab.addEventListener('click', ( event ) => {
                event.preventDefault();
                activateTab( tab, tabs, panels );
            } );

            tab.addEventListener('keydown', ( event ) => handleTabKeyboardNavigation( event, tabs ) );
        } );

        const initialTab = tabs.find( ( candidate ) => candidate.classList.contains('is-active') ) || tabs[0];

        if ( initialTab ) {
            activateTab( initialTab, tabs, panels );
        }
    } );
};

const initPhonePopup = () => {
    const popup = document.querySelector('[data-ls-phone-popup]');

    if ( ! popup || popup.dataset.lsPhonePopupInit === 'true' ) {
        return;
    }

    popup.dataset.lsPhonePopupInit = 'true';

    const dialog = popup.querySelector('.ls-phone-popup__dialog');
    const closers = Array.from( popup.querySelectorAll('[data-ls-phone-popup-close]') );
    const formState = popup.querySelector('[data-ls-phone-popup-state="form"]');
    const successState = popup.querySelector('[data-ls-phone-popup-state="success"]');
    const focusSelector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let opener = null;

    const setState = ( state ) => {
        if ( ! formState || ! successState ) {
            return;
        }

        const isSuccess = state === 'success';
        formState.hidden = isSuccess;
        successState.hidden = ! isSuccess;
    };

    const openPopup = ( event ) => {
        event.preventDefault();
        opener = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
        setState('form');
        popup.hidden = false;
        document.body.classList.add('ls-phone-popup-open');
        document.body.style.overflow = 'hidden';

        const firstFocusable = dialog?.querySelector( focusSelector );
        if ( firstFocusable instanceof HTMLElement ) {
            window.requestAnimationFrame( () => firstFocusable.focus() );
        }
    };

    const closePopup = () => {
        const cf7Form = popup.querySelector('.wpcf7 form');

        popup.hidden = true;
        document.body.classList.remove('ls-phone-popup-open');
        document.body.style.overflow = '';
        setState('form');

        if ( cf7Form instanceof HTMLFormElement ) {
            cf7Form.reset();
            cf7Form.classList.remove('sent', 'failed', 'invalid', 'unaccepted', 'spam');
            cf7Form.setAttribute('data-status', 'init');
        }

        if ( opener instanceof HTMLElement ) {
            opener.focus();
        }
    };

    document.querySelectorAll('.umow .wp-block-buttons').forEach( ( buttonsWrap ) => {
        const secondaryButtons = Array.from( buttonsWrap.querySelectorAll('.wp-block-button.is-style-ls-secondary > .wp-block-button__link'));
        const trigger = secondaryButtons[1];

        if ( ! trigger ) {
            return;
        }

        trigger.addEventListener('click', openPopup );
    } );

    closers.forEach( ( closer ) => {
        closer.addEventListener('click', closePopup );
    } );

    document.addEventListener('keydown', ( event ) => {
        if ( popup.hidden ) {
            return;
        }

        if ( event.key === 'Escape' || event.key === 'Esc' ) {
            event.preventDefault();
            closePopup();
        }
    } );

    document.addEventListener('wpcf7mailsent', ( event ) => {
        const form = event?.target;

        if ( ! form || ! popup.contains( form ) ) {
            return;
        }

        setState('success');
    } );
};

const initAuthorLabel = () => {
    const htmlLang = ( document.documentElement.getAttribute( 'lang' ) || '' ).toLowerCase();
    const labelText = htmlLang.startsWith( 'pl' ) ? 'Autor' : 'Author';

    const authorNameNodes = document.querySelectorAll(
        [
            '.wp-block-post-author-name',
            '.wp-block-post-author__name',
            '.wp-block-post-author .wp-block-post-author-name',
            '.wp-block-post-author .wp-block-post-author__name',
        ].join( ', ' )
    );

    authorNameNodes.forEach( ( authorNameNode ) => {
        if ( ! authorNameNode || authorNameNode.dataset.lsAuthorLabelInit === 'true' ) {
            return;
        }

        authorNameNode.dataset.lsAuthorLabelInit = 'true';

        if ( authorNameNode.previousElementSibling?.classList?.contains( 'ls-author-label' ) ) {
            return;
        }

        const labelNode = document.createElement( 'div' );
        labelNode.className = 'ls-author-label';
        labelNode.textContent = labelText;

        authorNameNode.parentNode?.insertBefore( labelNode, authorNameNode );
    } );
};

const initSectionRightImageDetection = () => {
    const updateSectionSideClasses = () => {
        document.querySelectorAll( '.ls-section' ).forEach( ( section ) => {
            const columns = section.querySelectorAll( '.wp-block-columns' );

            const hasRightSideImage = Array.from( columns ).some( ( columnsNode ) => {
                const columnNodes = columnsNode.querySelectorAll( ':scope > .wp-block-column' );
                const rightColumn = columnNodes[ columnNodes.length - 1 ];

                if ( ! rightColumn ) {
                    return false;
                }

                return Boolean( rightColumn.querySelector( 'img, picture img' ) );
            } );

            section.classList.toggle( 'rev-side', hasRightSideImage );
        } );
    };

    let resizeRaf = null;
    const handleResize = () => {
        if ( resizeRaf ) {
            window.cancelAnimationFrame( resizeRaf );
        }

        resizeRaf = window.requestAnimationFrame( () => {
            updateSectionSideClasses();
            resizeRaf = null;
        } );
    };

    updateSectionSideClasses();
    window.addEventListener( 'resize', handleResize );
};

document.addEventListener('DOMContentLoaded', () => {
    initStickyHeader();
    initPrimaryMenu();
    initMegaMenu();
    initFaqAccordion();
    initTabs();
    initPhonePopup();
    initAuthorLabel();
    initSliders();
    initTestimonialsSlider();
    initPatientReviews();
    initBeforeAfterSliders();
    initSectionRightImageDetection();
} );

window.addEventListener('load', () => {
    initAuthorLabel();
    initSliders();
    initTestimonialsSlider();
    initPatientReviews();
    initBeforeAfterSliders();
});

console.info('[wp-webpack-theme is live]');
